import { type FC, useEffect, useState } from "react"

import { footerSocialPlatformLabels, type LandingPageFooterBlock } from "@forento/core/models/landing"
import { footerSocialPlatforms, landingPageCopyCharacterLimits } from "@forento/core/schemas/landing"

import EditableField from "./EditableField"
import EditorShell from "./EditorShell"

type Props = {
	block: LandingPageFooterBlock
	onChange: (block: LandingPageFooterBlock) => void
	onClose: () => void
}

const FooterBlockEditor: FC<Props> = ({ block, onChange, onClose }) => {
	const [companyName, setCompanyName] = useState(block.content.companyName ?? "")
	const [address, setAddress] = useState(block.content.address ?? "")
	const [email, setEmail] = useState(block.content.email ?? "")
	const [socialUrls, setSocialUrls] = useState<Record<string, string>>(() => {
		const urls: Record<string, string> = {}
		for (const link of block.content.socialLinks ?? []) {
			urls[link.platform] = link.url
		}
		return urls
	})

	useEffect(() => {
		const socialLinks = footerSocialPlatforms
			.filter(platform => (socialUrls[platform] ?? "").trim().length > 0)
			.map(platform => ({ platform, url: (socialUrls[platform] ?? "").trim() }))

		onChange({
			type: "footer",
			content: {
				companyName: companyName.trim() || undefined,
				address: address.trim() || undefined,
				email: email.trim() || undefined,
				socialLinks: socialLinks.length > 0 ? socialLinks : undefined,
			},
		})
	}, [address, companyName, email, onChange, socialUrls])

	return (
		<EditorShell onClose={onClose}>
			<footer className="mt-16 bg-gray-900 px-6 py-12 sm:mt-20 lg:px-8">
				<div className="mx-auto max-w-7xl">
					<div className="flex flex-col gap-8 md:flex-row md:items-start md:justify-between">
						<div className="flex flex-col gap-2">
							<EditableField
								value={companyName}
								onChange={setCompanyName}
								placeholder="Company name"
								maxCharacters={landingPageCopyCharacterLimits.title}
								counterClassName="text-left"
								inputClassName="font-semibold text-lg text-white placeholder:text-white/30 placeholder:italic"
							/>
							<EditableField
								value={address}
								onChange={setAddress}
								placeholder="Address"
								multiline
								minRows={1}
								maxCharacters={landingPageCopyCharacterLimits.description}
								counterClassName="text-left"
								inputClassName="whitespace-pre-line text-gray-400 text-sm placeholder:text-gray-600 placeholder:italic"
							/>
							<EditableField
								value={email}
								onChange={setEmail}
								placeholder="Email"
								inputClassName="text-gray-400 text-sm placeholder:text-gray-600 placeholder:italic"
							/>
						</div>

						<div className="flex flex-col gap-2">
							{footerSocialPlatforms.map(platform => {
								const isActive = (socialUrls[platform] ?? "").trim().length > 0
								return (
									<div key={platform} className="flex items-center gap-2">
										<span
											className={
												isActive
													? "w-20 shrink-0 rounded bg-white/10 px-2 py-1 text-center text-xs text-gray-400"
													: "w-20 shrink-0 rounded bg-white/5 px-2 py-1 text-center text-xs text-gray-600"
											}
										>
											{footerSocialPlatformLabels[platform]}
										</span>
										<EditableField
											value={socialUrls[platform] ?? ""}
											onChange={value => setSocialUrls(prev => ({ ...prev, [platform]: value }))}
											placeholder="Paste URL to activate"
											inputClassName="text-gray-400 text-xs placeholder:text-gray-700"
										/>
									</div>
								)
							})}
						</div>
					</div>

					<div className="mt-8 flex flex-col gap-2 border-t border-white/10 pt-8 sm:flex-row sm:items-center sm:justify-between">
						{companyName.trim() && (
							<p className="text-xs text-gray-500">
								&copy; {new Date().getFullYear()} {companyName.trim()}. All rights reserved.
							</p>
						)}
						<nav aria-label="Footer policy links" className="flex flex-wrap gap-x-4 gap-y-2">
							<span className="text-xs text-gray-500">Terms of Service</span>
							<span className="text-xs text-gray-500">Privacy Policy</span>
						</nav>
					</div>
				</div>
			</footer>
		</EditorShell>
	)
}

export default FooterBlockEditor
